import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CustomIcon,
  DropdownMenu,
  showNotif,
  SnackbarAlert
} from '@p81-common/ui';
import IconButton from '@mui/material/IconButton';
import { Heading, Text } from 'components/common/Elements/Elements';
import { postRequest } from 'utils/requests';
import { access, hasAccess, resources } from 'utils/permissions';
import { links } from 'data/constants';
import {
  EDGE_DETAIL_URL,
  EDGE_RESTART_SUFFIX,
  EDGE_DEPLOY_SUFFIX,
  EdgeActionResponse,
  EdgeDetailResponse
} from '../helpers/configurates';
import ConfirmationModal from './ConfirmationModal';
import EdgeStatusBadge from './EdgeStatusBadge';
import { createSnackbarAlertClasses } from './styles';
import styles from '../styles.module.scss';

interface EdgeInfoCardProps {
  edge: EdgeDetailResponse;
  edgeId: string;
}

type ActionType = 'restart' | 'deploy' | null;

const DR_FLAG_MAP: Record<string, string> = {
  US: 'usa',
  EU: 'europe',
  IN: 'india',
  AU: 'australia',
  CA: 'canada'
};

const DataResidencyFlag = ({ dr }: { dr: string }) => {
  const flagName = DR_FLAG_MAP[dr?.toUpperCase()];
  if (!flagName) return <>{dr}</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <CustomIcon
        src={`${links.imagesBaseUrl}/jarvis/Jarvis flags/${flagName}.svg`}
        color=""
      />
      {dr}
    </span>
  );
};

const EdgeInfoCard = ({ edge, edgeId }: EdgeInfoCardProps) => {
  const { t } = useTranslation('edges');
  const snackbarStyles = createSnackbarAlertClasses();
  const [activeAction, setActiveAction] = useState<ActionType>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    action: 'restart' | 'deploy' | null;
    datadogUrl?: string;
  }>({ open: false, action: null });
  const menuAnchorRef = useRef<HTMLButtonElement>(null);

  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  const hasWriteAccess = hasAccess([resources.NETWORK_EDGE, access.WRITE]);

  const INFO_CELLS: {
    title: string;
    getValue: (edge: EdgeDetailResponse) => React.ReactNode;
  }[] = [
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.DR'),
      getValue: e => <DataResidencyFlag dr={e.data_residency} />
    },
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.REGION_NAME'),
      getValue: e => e.region_name
    },
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.VERSION'),
      getValue: e => e.edge_version
    },
    { title: t('EDGE_INFO_CARD.INFO_CELLS.EDGE_IP'), getValue: e => e.edge_ip },
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.STATUS'),
      getValue: e => <EdgeStatusBadge status={e.status} />
    },
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.TYPE'),
      getValue: e => e.machine_type
    },
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.SIZE'),
      getValue: e => e.machine_size
    },
    {
      title: t('EDGE_INFO_CARD.INFO_CELLS.PROVIDER'),
      getValue: e => e.region_provider
    }
  ];

  const modalConfig: Record<
    'restart' | 'deploy',
    { title: string; message: string; confirmLabel: string }
  > = {
    restart: {
      title: t('EDGE_INFO_CARD.RESTART.TITLE'),
      message: t('EDGE_INFO_CARD.RESTART.MESSAGE', { edgeId }),
      confirmLabel: t('EDGE_INFO_CARD.RESTART.CONFIRM_LABEL')
    },
    deploy: {
      title: t('EDGE_INFO_CARD.DEPLOY.TITLE'),
      message: t('EDGE_INFO_CARD.DEPLOY.MESSAGE', { edgeId }),
      confirmLabel: t('EDGE_INFO_CARD.DEPLOY.CONFIRM_LABEL')
    }
  };

  const handleAction = async () => {
    if (!activeAction) return;

    const suffix =
      activeAction === 'restart' ? EDGE_RESTART_SUFFIX : EDGE_DEPLOY_SUFFIX;
    const url = `${EDGE_DETAIL_URL}/${edgeId}${suffix}`;

    setIsLoading(true);
    try {
      const res: EdgeActionResponse = await postRequest(url, {});
      setSnackbar({
        open: true,
        action: activeAction,
        datadogUrl: res?.body?.datadog_url
      });
      setActiveAction(null);
    } catch (e: any) {
      const errorMessage =
        e?.body?.message || e?.message || t('EDGE_INFO_CARD.ERROR_OCCURRED');
      showNotif(errorMessage, { alertProps: { severity: 'error' } });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.cellBorder}>
      <ul className={styles.cellWrap}>
        <li className={`${styles.cell} ${styles.edgeNameCell}`}>
          <Heading size={2} weight="m" className="">
            {edge.edge_id}
          </Heading>
        </li>

        {INFO_CELLS.map(({ title, getValue }) => (
          <li className={styles.cell} key={title}>
            <Heading size={4} weight="r" className={styles.cellTitle}>
              {title}
            </Heading>
            <Text type="s" size={6} weight="m" className={styles.cellValue}>
              {getValue(edge) ?? <>&nbsp;</>}
            </Text>
          </li>
        ))}

        {hasWriteAccess && (
          <li className={`${styles.cell} ${styles.actionsCell}`}>
            <div className={styles.menuWrap}>
              <IconButton ref={menuAnchorRef} onClick={() => setMenuOpen(true)}>
                <CustomIcon
                  src={`${links.imagesBaseUrl}/icons/icon_3dots.svg`}
                />
              </IconButton>
              {menuOpen && (
                <DropdownMenu
                  anchorEl={menuAnchorRef.current}
                  arrowSide="topRight"
                  open={menuOpen}
                  onClose={() => setMenuOpen(false)}
                  handleClick={name => () => {
                    setMenuOpen(false);
                    setActiveAction(name as ActionType);
                  }}
                  leftAddition={-152}
                  anchorElTopAddition={8}
                  listItems={[
                    {
                      name: 'restart',
                      textNode: <span>{t('EDGE_INFO_CARD.RESTART.TITLE')}</span>
                    },
                    {
                      name: 'deploy',
                      textNode: <span>{t('EDGE_INFO_CARD.DEPLOY.TITLE')}</span>
                    }
                  ]}
                />
              )}
            </div>
          </li>
        )}
      </ul>

      {activeAction && (
        <ConfirmationModal
          open={true}
          title={modalConfig[activeAction].title}
          message={modalConfig[activeAction].message}
          confirmLabel={modalConfig[activeAction].confirmLabel}
          onConfirm={handleAction}
          onClose={() => setActiveAction(null)}
          isLoading={isLoading}
        />
      )}

      <SnackbarAlert
        open={snackbar.open}
        autoHideDuration={10000}
        onClose={closeSnackbar}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        alertProps={{
          severity: 'success',
          className: snackbarStyles.alert,
          onClose: closeSnackbar,
          action: snackbar.datadogUrl ? (
            <a
              href={snackbar.datadogUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="small"
                color="secondary"
                className={snackbarStyles.button}
              >
                {t('EDGE_INFO_CARD.VIEW_LOGS')}
              </Button>
            </a>
          ) : undefined
        }}
      >
        {snackbar.action === 'restart'
          ? t('EDGE_INFO_CARD.RESTART.SUCCESS', { edgeId })
          : snackbar.action === 'deploy'
          ? t('EDGE_INFO_CARD.DEPLOY.SUCCESS', { edgeId })
          : ''}
      </SnackbarAlert>
    </div>
  );
};

export default EdgeInfoCard;
